import type {Button as SanityButton} from "@repo/sanity-utils/schema/types";
import {stegaClean} from "next-sanity";
import type {PortableTextBlock} from "next-sanity";

export const PRIVACY_POLICY_ANNOUNCEMENT_SLUG = "privacy-policy-2026";

/** Button link that runs privacy-policy acceptance instead of navigating. */
export const ACCEPT_PRIVACY_POLICY_BUTTON_LINK = "{{acceptPrivacyPolicy}}";

export type PrivacyPolicyAnnouncementButton = SanityButton & {_key: string};

export type PrivacyPolicyAnnouncement = {
	title: string;
	body: PortableTextBlock[];
	buttons: PrivacyPolicyAnnouncementButton[];
};

type RawAnnouncementButton = {
	_key?: string;
	text?: string;
	link?: string;
	variant?: SanityButton["variant"];
	icon?: SanityButton["icon"];
	iconPosition?: SanityButton["iconPosition"];
	size?: SanityButton["size"];
	disabled?: boolean;
};

type RawAnnouncement = {
	title?: string | null;
	body?: PortableTextBlock[] | null;
	buttons?: RawAnnouncementButton[] | null;
};

export function isAcceptPrivacyPolicyButton(link: string | undefined): boolean {
	return stegaClean(link) === ACCEPT_PRIVACY_POLICY_BUTTON_LINK;
}

export function toPrivacyPolicyAnnouncement(
	data: unknown
): PrivacyPolicyAnnouncement | null {
	if (!data || typeof data !== "object") {
		return null;
	}

	const record = data as RawAnnouncement;
	if (typeof record.title !== "string" || record.title.trim() === "") {
		return null;
	}
	if (!Array.isArray(record.body) || record.body.length === 0) {
		return null;
	}

	const buttons = (record.buttons ?? []).flatMap((button) => {
		if (!button._key) {
			return [];
		}
		const normalized: PrivacyPolicyAnnouncementButton = {
			_type: "button",
			_key: button._key,
			text: button.text,
			link: button.link,
			variant: button.variant,
			icon: button.icon,
			iconPosition: button.iconPosition,
			size: button.size ?? "default",
			disabled: button.disabled,
		};
		return [normalized];
	});

	return {
		title: record.title,
		body: record.body,
		buttons,
	};
}
